import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Box, Card, CircularProgress, IconButton, LinearProgress, Sheet, Stack, Typography } from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

import { useAdmin } from "../../AdminContext";
import { APIAdminQuizStats } from "../../api_admin";

function StatCard({ label, value, color = "primary" }) {
  return (
    <Card sx={{ borderRadius: "xl", flex: 1, minWidth: 120 }}>
      <Typography level="body-xs" sx={{ color: "text.tertiary" }}>{label}</Typography>
      <Typography level="h2" sx={{ color: `${color}.600`, fontWeight: 800 }}>{value}</Typography>
    </Card>
  );
}

export default function AdminQuizStatsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { adminToken } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!adminToken) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const data = await APIAdminQuizStats(adminToken, id);
        if (alive) setStats(data.stats);
      } catch (e) { /* ignore */ } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [adminToken, id]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!stats) return <Typography sx={{ py: 6, textAlign: "center", color: "text.tertiary" }}>Нет данных</Typography>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton variant="soft" color="neutral" onClick={() => navigate("/admin")}><ArrowBackRoundedIcon /></IconButton>
        <Box>
          <Typography level="h3">Статистика</Typography>
          <Typography level="body-xs" sx={{ fontFamily: "monospace", color: "text.tertiary" }}>{stats.code}</Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <StatCard label="Начали" value={stats.started} />
        <StatCard label="Завершили" value={stats.completed} color="success" />
        <StatCard label="Конверсия" value={`${stats.completion_rate}%`} color="primary" />
      </Stack>

      <Typography level="title-md" sx={{ mb: 1.5 }}>По вопросам</Typography>
      <Stack spacing={2}>
        {(stats.questions || []).map((q, i) => {
          const totalChoice = q.question_type !== "scale"
            ? (q.options || []).reduce((s, o) => s + (o.count || 0), 0)
            : 0;
          return (
            <Card key={q.question_id} sx={{ borderRadius: "xl" }}>
              <Typography level="body-sm" sx={{ fontWeight: 700 }}>{i + 1}. {q.text}</Typography>

              {q.question_type === "scale" ? (
                <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                  <Box><Typography level="body-xs" sx={{ color: "text.tertiary" }}>Среднее</Typography><Typography level="title-md">{q.scale?.avg ?? "—"}</Typography></Box>
                  <Box><Typography level="body-xs" sx={{ color: "text.tertiary" }}>Мин</Typography><Typography level="title-md">{q.scale?.min ?? "—"}</Typography></Box>
                  <Box><Typography level="body-xs" sx={{ color: "text.tertiary" }}>Макс</Typography><Typography level="title-md">{q.scale?.max ?? "—"}</Typography></Box>
                  <Box><Typography level="body-xs" sx={{ color: "text.tertiary" }}>Ответов</Typography><Typography level="title-md">{q.scale?.answers ?? 0}</Typography></Box>
                </Stack>
              ) : (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {(q.options || []).map((o) => {
                    const pct = totalChoice ? Math.round((o.count / totalChoice) * 100) : 0;
                    return (
                      <Box key={o.option_id}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography level="body-sm">{o.text}</Typography>
                          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>{o.count} · {pct}%</Typography>
                        </Stack>
                        <LinearProgress determinate value={pct} thickness={6} sx={{ borderRadius: 10 }} />
                      </Box>
                    );
                  })}
                  {(q.options || []).length === 0 && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Нет данных по вариантам</Typography>
                  )}
                </Stack>
              )}
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
}